import {
  BoxGeometry,
  Box3,
  CylinderGeometry,
  Group,
  IcosahedronGeometry,
  Mesh,
  MeshLambertMaterial,
  SphereGeometry,
  type BufferGeometry,
  type Material,
  type Object3D,
} from 'three';

/**
 * JEFE-2 El Jinete (GDD §9.2, §4.3, §3.4, issue #111): a parody caricature, never a real name or insignia. A rider in
 * an impeccable white suit on a paso fino horse with cardboard wings (they look like props) and a propeller tied to
 * the saddle. Low-poly with flat shading, from primitives, about 3.6 x 2.6 u. The retreat set is a generic parade of
 * the Feria de Cali (floats and dancers) where the horse refuses to go on and lands: nobody crashes (GDD §3.4).
 */
export interface Jefe2Model {
  root: Group;
  horse: Group;
  wings: Group;
  propeller: Mesh;
  rider: Group;
  /** The four legs of the horse, front left to back right, for the four-beat gait. */
  legs: Group[];
  /** The hand that reaches the moustache or waves, depending on the pose. */
  arm: Group;
  /** The parade of the Feria de Cali for the retreat, and the platform the horse lands on. */
  parade: Group;
  floor: Mesh;
  dispose(): void;
}

export const JEFE_2_COLORS = {
  suit: 0xf4f3ee,
  shirt: 0x2fa7a0,
  skin: 0xd9a37a,
  hat: 0xe6d9b8,
  horse: 0x8a5a3c,
  mane: 0x3a2a20,
  saddle: 0x3d5a8a,
  wing: 0xc9a66b,
  wingFold: 0xa88650,
  propeller: 0x4a4f55,
  rope: 0xc9b896,
  floorA: 0x4f9d5d,
  floatA: 0xf2c230,
  floatB: 0x2fa7a0,
  floatC: 0xe39ab9,
  dancer: 0xf08a24,
  banner: 0x2459a8,
} as const;

/** Distance from the origin of the model to the bottom of its hooves: it rests on the parade floor at this height. */
export const HOOF_OFFSET = 1.2;

export function buildJefe2(): Jefe2Model {
  const geometries: BufferGeometry[] = [];
  const materials: Material[] = [];
  const paint = (color: number) => {
    const material = new MeshLambertMaterial({ color, flatShading: true });
    materials.push(material);
    return material;
  };
  const part = <G extends BufferGeometry>(geometry: G, color: number, at: [number, number, number], parent?: Object3D) => {
    geometries.push(geometry);
    const mesh = new Mesh(geometry, paint(color));
    mesh.position.set(...at);
    parent?.add(mesh);
    return mesh;
  };

  const root = new Group();
  root.name = 'boss:JEFE-2';
  root.userData.pose = 'PASO_FINO';

  // The horse: barrel body, neck, head with a mane and four legs that move in the four-beat gait.
  const horse = new Group();
  horse.name = 'boss:JEFE-2:horse';
  const body = part(new IcosahedronGeometry(0.7, 1), JEFE_2_COLORS.horse, [0, -0.2, 0], horse);
  body.scale.set(1.5, 0.85, 0.8);
  const neck = part(new BoxGeometry(0.4, 0.8, 0.35), JEFE_2_COLORS.horse, [-0.95, 0.15, 0], horse);
  neck.rotation.z = 0.5;
  part(new BoxGeometry(0.7, 0.3, 0.3), JEFE_2_COLORS.horse, [-1.4, 0.45, 0], horse);
  part(new BoxGeometry(0.15, 0.6, 0.12), JEFE_2_COLORS.mane, [-0.85, 0.35, 0], horse).rotation.z = 0.5;
  part(new BoxGeometry(0.1, 0.5, 0.1), JEFE_2_COLORS.mane, [1.0, -0.1, 0], horse).rotation.z = -0.6;
  part(new BoxGeometry(0.7, 0.12, 0.7), JEFE_2_COLORS.saddle, [0.0, 0.18, 0], horse);
  const legs: Group[] = [];
  for (const [x, z] of [[-0.6, 0.25], [-0.6, -0.25], [0.6, 0.25], [0.6, -0.25]] as const) {
    const leg = new Group();
    leg.position.set(x, -0.5, z);
    part(new BoxGeometry(0.14, 0.7, 0.14), JEFE_2_COLORS.horse, [0, -0.35, 0], leg);
    part(new BoxGeometry(0.18, 0.08, 0.18), JEFE_2_COLORS.mane, [0, -0.72, 0], leg);
    horse.add(leg);
    legs.push(leg);
  }
  root.add(horse);

  // Cardboard wings: flat panels with fold lines that flap with the beat.
  const wings = new Group();
  wings.name = 'boss:JEFE-2:wings';
  for (const side of [1, -1]) {
    const wing = new Group();
    wing.position.set(0.1, 0.35, 0.35 * side);
    wing.rotation.x = side * 0.7;
    part(new BoxGeometry(1.4, 0.05, 0.9), JEFE_2_COLORS.wing, [0.2, 0.45, side * 0.35], wing);
    part(new BoxGeometry(1.4, 0.06, 0.06), JEFE_2_COLORS.wingFold, [0.2, 0.47, side * 0.35], wing);
    wings.add(wing);
  }
  root.add(wings);

  // The propeller tied to the saddle (a small hub and two blades; it spins with render time).
  const propeller = part(new BoxGeometry(0.06, 0.9, 0.08), JEFE_2_COLORS.propeller, [0.55, 0.35, 0]);
  propeller.name = 'boss:JEFE-2:propeller';
  part(new BoxGeometry(0.06, 0.08, 0.9), JEFE_2_COLORS.propeller, [0, 0, 0], propeller);
  part(new CylinderGeometry(0.1, 0.1, 0.12, 6), JEFE_2_COLORS.propeller, [0, 0, 0], propeller).rotation.x = Math.PI / 2;
  root.add(propeller);

  // The rider: white suit, teal shirt, a pale hat and the arm that waves or reaches for the moustache.
  const rider = new Group();
  rider.name = 'boss:JEFE-2:rider';
  rider.position.set(0, 0.55, 0);
  part(new BoxGeometry(0.4, 0.6, 0.3), JEFE_2_COLORS.suit, [0, 0.3, 0], rider);
  part(new BoxGeometry(0.12, 0.4, 0.32), JEFE_2_COLORS.shirt, [0, 0.35, 0], rider);
  part(new SphereGeometry(0.2, 6, 4), JEFE_2_COLORS.skin, [0, 0.8, 0], rider);
  part(new CylinderGeometry(0.34, 0.34, 0.04, 8), JEFE_2_COLORS.hat, [0, 0.95, 0], rider);
  part(new CylinderGeometry(0.2, 0.22, 0.16, 8), JEFE_2_COLORS.hat, [0, 1.03, 0], rider);
  const arm = new Group();
  arm.name = 'boss:JEFE-2:arm';
  arm.position.set(0.25, 0.5, 0.15);
  part(new BoxGeometry(0.12, 0.45, 0.12), JEFE_2_COLORS.suit, [0, -0.15, 0], arm);
  rider.add(arm);
  root.add(rider);

  // The Feria de Cali parade: a floor, two floats and a few dancers under a banner. Generic, nobody is a target.
  const parade = new Group();
  parade.name = 'boss:JEFE-2:parade';
  const floor = part(new BoxGeometry(9, 0.1, 2.6), JEFE_2_COLORS.floorA, [0, 0.05, 0], parade);
  floor.name = 'boss:JEFE-2:parade-floor';
  part(new BoxGeometry(2.2, 1.0, 1.4), JEFE_2_COLORS.floatA, [-3.2, 0.6, -0.6], parade);
  part(new BoxGeometry(2.2, 1.4, 1.4), JEFE_2_COLORS.floatB, [3.2, 0.8, -0.6], parade);
  part(new SphereGeometry(0.5, 6, 4), JEFE_2_COLORS.floatC, [3.2, 1.8, -0.6], parade);
  for (const x of [-1.6, -0.9, 1.0, 1.7]) {
    part(new CylinderGeometry(0.14, 0.2, 0.7, 6), JEFE_2_COLORS.dancer, [x, 0.45, -0.3], parade);
    part(new SphereGeometry(0.13, 6, 4), JEFE_2_COLORS.skin, [x, 0.95, -0.3], parade);
  }
  part(new BoxGeometry(8, 0.3, 0.1), JEFE_2_COLORS.banner, [0, 3.2, -1.2], parade);
  part(new BoxGeometry(0.1, 3.1, 0.1), JEFE_2_COLORS.propeller, [-4, 1.6, -1.2], parade);
  part(new BoxGeometry(0.1, 3.1, 0.1), JEFE_2_COLORS.propeller, [4, 1.6, -1.2], parade);
  parade.visible = false;

  root.visible = false;
  return {
    root,
    horse,
    wings,
    propeller,
    rider,
    legs,
    arm,
    parade,
    floor,
    dispose() {
      for (const geometry of geometries) geometry.dispose();
      for (const material of materials) material.dispose();
    },
  };
}

/** The visual size of the model (u) with the propeller and the wings, for the hitbox of the body (70 to 80 %). */
export function visualSize(model: Jefe2Model): { width: number; height: number } {
  const box = new Box3().setFromObject(model.root);
  return { width: box.max.x - box.min.x, height: box.max.y - box.min.y };
}
